import React, { useState, useCallback } from 'react';
import { fileDatabaseAPI } from '../../services/api';
import DashboardLayout from '../../components/layouts/DashboardLayout';
import { Database, Upload, Search, AlertTriangle, CheckCircle, Info, Loader2, FolderOpen, Copy, ChevronDown, ChevronUp, Shield, FileCode, Cpu, Car, HardDrive, Fuel } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const ConfidenceBadge = ({ confidence, level }) => {
  const getColor = () => {
    if (confidence >= 95) return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30';
    if (confidence >= 80) return 'bg-green-500/20 text-green-400 border-green-500/30';
    if (confidence >= 60) return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
    return 'bg-red-500/20 text-red-400 border-red-500/30';
  };
  const getIcon = () => {
    if (confidence >= 95) return <Shield className="w-3 h-3" />;
    if (confidence >= 80) return <CheckCircle className="w-3 h-3" />;
    if (confidence >= 60) return <Info className="w-3 h-3" />;
    return <AlertTriangle className="w-3 h-3" />;
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold border ${getColor()}`}>
      {getIcon()} {confidence}%
    </span>
  );
};

const MatchCard = ({ match, index }) => {
  const [expanded, setExpanded] = useState(false);
  const copyPath = () => {
    const folder = match.source_folder || '';
    const file = match.project_file || '';
    const path = folder ? `${folder}\\${file}` : file;
    navigator.clipboard.writeText(path);
    toast.success('Pfad kopiert!');
  };
  return (
    <div className={`border rounded-lg p-3 transition-all hover:border-[#EC2229]/30 ${
      match.confidence >= 95 ? 'border-emerald-500/30 bg-emerald-500/5' :
      match.confidence >= 80 ? 'border-green-500/20 bg-green-500/5' :
      match.confidence >= 60 ? 'border-amber-500/20 bg-amber-500/5' :
      'border-white/10 bg-white/5'
    }`} data-testid={`match-card-${index}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <ConfidenceBadge confidence={match.confidence} level={match.match_level} />
            <span className="text-xs text-slate-500">Level {match.match_level}</span>
            {match.vehicle_manufacturer && <span className="text-xs text-slate-400 font-medium">{match.vehicle_manufacturer}</span>}
            {match.vehicle_series && <span className="text-xs text-slate-500">{match.vehicle_series}</span>}
            {match.engine_variant && <span className="text-xs text-slate-500">{match.engine_variant}</span>}
          </div>
          <div className="flex items-center gap-2 mb-1">
            {match.ecu_type && <span className="text-xs font-mono bg-white/10 px-1.5 py-0.5 rounded text-blue-300">{match.ecu_type}</span>}
            {match.ecu_nr_prod && <span className="text-xs font-mono text-slate-400">{match.ecu_nr_prod}</span>}
            {match.software_version && <span className="text-xs font-mono text-slate-500">SW:{match.software_version}</span>}
          </div>
          <p className="text-xs text-emerald-400/80 italic">{match.match_reason}</p>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button onClick={copyPath} className="p-1.5 hover:bg-white/10 rounded transition-colors" title="Pfad kopieren">
            <Copy className="w-3.5 h-3.5 text-slate-400" />
          </button>
          <button onClick={() => setExpanded(!expanded)} className="p-1.5 hover:bg-white/10 rounded transition-colors">
            {expanded ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
          </button>
        </div>
      </div>
      {expanded && (
        <div className="mt-2 pt-2 border-t border-white/5 space-y-1.5">
          <div className="flex items-center gap-2 bg-black/20 rounded p-2">
            <FolderOpen className="w-3.5 h-3.5 text-[#EC2229] flex-shrink-0" />
            <span className="text-xs font-mono text-slate-300 break-all">{match.source_folder}\{match.project_file}</span>
          </div>
          <div className="grid grid-cols-2 gap-1 text-xs">
            {match.ecu_manufacturer && <div><span className="text-slate-500">ECU Hersteller:</span> <span className="text-slate-300">{match.ecu_manufacturer}</span></div>}
            {match.ecu_nr_ecu && <div><span className="text-slate-500">ECU-Nr:</span> <span className="text-slate-300 font-mono">{match.ecu_nr_ecu}</span></div>}
            {match.sw_version && <div><span className="text-slate-500">SW-Version:</span> <span className="text-slate-300 font-mono">{match.sw_version}</span></div>}
            {match.project_size && <div><span className="text-slate-500">Groesse:</span> <span className="text-slate-300">{match.project_size}</span></div>}
            {match.fuel_type && <div><span className="text-slate-500">Kraftstoff:</span> <span className="text-slate-300">{match.fuel_type}</span></div>}
            {match.project_type && <div><span className="text-slate-500">Projektart:</span> <span className="text-slate-300">{match.project_type}</span></div>}
            <div><span className="text-slate-500">Quelle:</span> <span className="text-slate-300">{match.source_file}</span></div>
          </div>
          {match.version_types?.length > 0 && (
            <div className="flex gap-1 flex-wrap">
              {match.version_types.map((v, i) => (
                <span key={i} className="text-xs px-1.5 py-0.5 bg-white/5 border border-white/10 rounded text-slate-400">{v}</span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default function AdminFileSearchPage() {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [minConfidence, setMinConfidence] = useState(0);
  const [dragActive, setDragActive] = useState(false);

  const handleFileSelect = useCallback((selectedFile) => {
    if (selectedFile) {
      setFile(selectedFile);
      setResult(null);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragActive(false);
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) handleFileSelect(droppedFile);
  }, [handleFileSelect]);

  const runAnalysis = useCallback(async () => {
    if (!file) return;
    setLoading(true);
    try {
      const res = await fileDatabaseAPI.analyzeAndMatch(file, { max_results: 200 });
      setResult(res.data);
      if (res.data.total_matches === 0) {
        toast.info('Keine passenden Projekte gefunden');
      } else {
        toast.success(`${res.data.total_matches} passende Projekte gefunden`);
      }
    } catch (err) {
      console.error('Analysis failed:', err);
      toast.error('Fehler bei der Analyse: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  }, [file]);

  const analysis = result?.analysis;
  const matches = result?.matches || [];
  const filteredMatches = matches.filter(m => m.confidence >= minConfidence);
  const highConfidence = filteredMatches.filter(m => m.confidence >= 80);
  const medConfidence = filteredMatches.filter(m => m.confidence >= 60 && m.confidence < 80);
  const lowConfidence = filteredMatches.filter(m => m.confidence < 60);

  return (
    <DashboardLayout>
    <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-6" data-testid="admin-file-search-page">
      <div>
        <h1 className="text-2xl font-bold text-white">Datei-Suche</h1>
        <p className="text-sm text-slate-500 mt-1">ECU-Datei hochladen und passende Projekte in der Datenbank finden</p>
      </div>

      {/* File Upload Area */}
      <div
        className={`border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer ${
          dragActive ? 'border-[#EC2229] bg-[#EC2229]/5' :
          file ? 'border-emerald-500/30 bg-emerald-500/5' :
          'border-white/10 bg-white/5 hover:border-white/20'
        }`}
        data-testid="file-drop-zone"
        onDragEnter={(e) => { e.preventDefault(); setDragActive(true); }}
        onDragLeave={(e) => { e.preventDefault(); setDragActive(false); }}
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
        onClick={() => document.getElementById('file-search-input').click()}
      >
        <input
          id="file-search-input"
          type="file"
          className="hidden"
          accept=".bin,.ori,.mod,.ols,.slave,.master"
          onChange={(e) => handleFileSelect(e.target.files?.[0])}
          data-testid="file-search-file-input"
        />
        {file ? (
          <div className="space-y-2">
            <FileCode className="w-10 h-10 text-emerald-400 mx-auto" />
            <p className="text-white font-medium">{file.name}</p>
            <p className="text-xs text-slate-500">{(file.size / (1024 * 1024)).toFixed(2)} MB</p>
            <p className="text-xs text-slate-600">Klicken um andere Datei zu waehlen</p>
          </div>
        ) : (
          <div className="space-y-2">
            <Upload className="w-10 h-10 text-slate-500 mx-auto" />
            <p className="text-slate-400">ECU-Datei hierher ziehen oder klicken</p>
            <p className="text-xs text-slate-600">.bin, .ori, .mod, .ols</p>
          </div>
        )}
      </div>

      {/* Analyze Button */}
      {file && !loading && (
        <Button
          onClick={runAnalysis}
          className="w-full bg-[#EC2229] hover:bg-[#EC2229]/80 text-white h-12 text-base"
          data-testid="analyze-button"
        >
          <Search className="w-5 h-5 mr-2" />
          Analysieren & Projekte suchen
        </Button>
      )}
      {loading && (
        <div className="flex items-center justify-center py-6 gap-3">
          <Loader2 className="w-6 h-6 text-[#EC2229] animate-spin" />
          <span className="text-slate-400">Analysiere Datei und durchsuche 2,5 Mio. Projekte...</span>
        </div>
      )}

      {/* Analysis Results */}
      {analysis && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* File Info Card */}
          <div className="glass-card p-4 space-y-3" data-testid="analysis-info">
            <h3 className="text-sm font-semibold text-white flex items-center gap-2">
              <Cpu className="w-4 h-4 text-[#EC2229]" />
              Erkannte Informationen
            </h3>
            <div className="space-y-2 text-sm">
              {analysis.vehicle_manufacturer && (
                <div className="flex items-center gap-2">
                  <Car className="w-3.5 h-3.5 text-slate-500" />
                  <span className="text-slate-500">Hersteller:</span>
                  <span className="text-white font-medium">{analysis.vehicle_manufacturer}</span>
                </div>
              )}
              {analysis.ecu_type && (
                <div className="flex items-center gap-2">
                  <Cpu className="w-3.5 h-3.5 text-slate-500" />
                  <span className="text-slate-500">ECU:</span>
                  <span className="text-white font-mono">{analysis.ecu_maker} {analysis.ecu_type}</span>
                </div>
              )}
              {analysis.fuel_type && (
                <div className="flex items-center gap-2">
                  <Fuel className="w-3.5 h-3.5 text-slate-500" />
                  <span className="text-slate-500">Kraftstoff:</span>
                  <span className="text-white">{analysis.fuel_type}</span>
                </div>
              )}
              {analysis.sw_version && (
                <div className="flex items-center gap-2">
                  <HardDrive className="w-3.5 h-3.5 text-slate-500" />
                  <span className="text-slate-500">SW:</span>
                  <span className="text-white font-mono">{analysis.sw_version}</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <HardDrive className="w-3.5 h-3.5 text-slate-500" />
                <span className="text-slate-500">Groesse:</span>
                <span className="text-white">{(result.file_size / 1024).toFixed(0)} KB</span>
              </div>
              <div className="mt-2 pt-2 border-t border-white/5">
                <span className="text-slate-500 text-xs">Erkennungsrate:</span>
                <div className="w-full bg-white/5 rounded-full h-2 mt-1">
                  <div className="h-2 rounded-full bg-[#EC2229]" style={{ width: `${analysis.confidence}%` }} />
                </div>
                <span className="text-xs text-slate-500">{analysis.confidence}%</span>
              </div>
            </div>
            {analysis.raw_identifiers?.length > 0 && (
              <div className="pt-2 border-t border-white/5">
                <p className="text-xs text-slate-500 mb-1">Erkannte Merkmale:</p>
                <div className="space-y-0.5">
                  {analysis.raw_identifiers.map((id, i) => (
                    <p key={i} className="text-xs font-mono text-slate-600 truncate" title={id}>{id}</p>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Search Params + Results */}
          <div className="lg:col-span-2 space-y-4">
            {/* Search Params */}
            {result.search_params && (
              <div className="bg-black/20 rounded-lg p-3 text-xs text-slate-400 flex flex-wrap gap-x-4 gap-y-1" data-testid="search-params-display">
                {result.search_params.ecu_nr_prod && <span>Prod-Nr: <span className="text-white font-mono">{result.search_params.ecu_nr_prod}</span></span>}
                {result.search_params.ecu_nr_ecu && <span>ECU-Nr: <span className="text-white font-mono">{result.search_params.ecu_nr_ecu}</span></span>}
                {result.search_params.software_version && <span>SW: <span className="text-white font-mono">{result.search_params.software_version}</span></span>}
                {result.search_params.ecu_type && <span>ECU: <span className="text-white font-mono">{result.search_params.ecu_type}</span></span>}
                {result.search_params.vehicle_manufacturer && <span>Marke: <span className="text-white">{result.search_params.vehicle_manufacturer}</span></span>}
                <span>Groesse: <span className="text-white">{(result.search_params.file_size / 1024).toFixed(0)} KB</span></span>
              </div>
            )}

            {/* Confidence Filter */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">Min. Konfidenz:</span>
              {[0, 60, 80, 95].map(v => (
                <button
                  key={v}
                  onClick={() => setMinConfidence(v)}
                  className={`text-xs px-2 py-1 rounded border transition-all ${minConfidence === v ? 'bg-[#EC2229]/20 border-[#EC2229]/30 text-[#EC2229]' : 'bg-white/5 border-white/10 text-slate-500 hover:text-white'}`}
                  data-testid={`filter-${v}`}
                >
                  {v === 0 ? 'Alle' : `${v}%+`}
                </button>
              ))}
              <span className="text-xs text-slate-600 ml-2">{filteredMatches.length} von {matches.length} Treffern</span>
            </div>

            {/* Results */}
            {filteredMatches.length > 0 ? (
              <div className="space-y-4 max-h-[700px] overflow-y-auto pr-1">
                {highConfidence.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Shield className="w-3.5 h-3.5 text-emerald-400" />
                      <span className="text-xs font-semibold text-emerald-400 uppercase tracking-wider">Hohe Sicherheit ({highConfidence.length})</span>
                    </div>
                    <div className="space-y-2">
                      {highConfidence.map((m, i) => <MatchCard key={`h-${i}`} match={m} index={i} />)}
                    </div>
                  </div>
                )}
                {medConfidence.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Info className="w-3.5 h-3.5 text-amber-400" />
                      <span className="text-xs font-semibold text-amber-400 uppercase tracking-wider">Mittlere Sicherheit ({medConfidence.length})</span>
                    </div>
                    <div className="space-y-2">
                      {medConfidence.map((m, i) => <MatchCard key={`m-${i}`} match={m} index={i + highConfidence.length} />)}
                    </div>
                  </div>
                )}
                {lowConfidence.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="w-3.5 h-3.5 text-red-400" />
                      <span className="text-xs font-semibold text-red-400 uppercase tracking-wider">Nur zur Info ({lowConfidence.length})</span>
                    </div>
                    <div className="space-y-2">
                      {lowConfidence.map((m, i) => <MatchCard key={`l-${i}`} match={m} index={i + highConfidence.length + medConfidence.length} />)}
                    </div>
                  </div>
                )}
              </div>
            ) : matches.length > 0 ? (
              <div className="text-center py-8">
                <Database className="w-8 h-8 text-slate-600 mx-auto mb-2" />
                <p className="text-sm text-slate-500">Keine Treffer mit min. {minConfidence}% Konfidenz</p>
                <p className="text-xs text-slate-600 mt-1">Versuche einen niedrigeren Filter</p>
              </div>
            ) : result ? (
              <div className="text-center py-8">
                <Database className="w-8 h-8 text-slate-600 mx-auto mb-2" />
                <p className="text-sm text-slate-500">Keine passenden Projekte gefunden</p>
                <p className="text-xs text-slate-600 mt-1">Die erkannten Parameter ergaben keine Treffer in der Datenbank</p>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
    </DashboardLayout>
  );
}
